import { Component, ContentChild, Directive, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AsyncPipe, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { MatCard, MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { ButtonDirective } from '../../directives/button.directive';
import { HostListenerService } from 'app/services/host-listener.service';
import { AvatarBlockModule } from '../../avatar-block/avatar-block.module';
import { AttachmentsCardComponent } from '../attachments-card/attachments-card.component';
import { CounterPipe } from '../../../../../pipes/collection/counter.pipe';
import { AnimatedDelayDirective, AnimatedDirective } from '../../directives/animated.directive';

export interface DetailsCardAction {
    title: string;
    action: string;
    callback: () => void;
    description?: string;
    svg?: string;
    icon?: string;
}

@UntilDestroy()
@Component({
    selector: 'app-details-card-container',
    templateUrl: './details-card-container.component.html',
    styleUrls: ['./details-card-container.component.scss'],
    standalone: true,
    host: { class: 'details-card-container' },
    imports: [
        AsyncPipe,
        MatCardModule,
        MatIconModule,
        MatMenuModule,
        NgIf,
        NgForOf,
        MatProgressBarModule,
        MatDividerModule,
        MatListModule,
        MatButtonModule,
        ButtonDirective,
        AvatarBlockModule,
        NgTemplateOutlet,
        CounterPipe,
        AnimatedDirective,
        AnimatedDelayDirective,
    ],
})
export class DetailsCardContainerComponent implements OnInit, OnChanges
{
    @Input() loadingSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    @Input() showActions = false;
    @Input() actions: DetailsCardAction[];
    @Input() actionLines: number = 1;
    @Output() actionClicked: EventEmitter<DetailsCardAction> = new EventEmitter<DetailsCardAction>();

    @HostBinding('class.stacked') stackedClass = true;
    @HostBinding('class.centered') centeredClass = false;

    @ContentChild(AttachmentsCardComponent) attachmentsCard!: AttachmentsCardComponent;

    isDesktopMode = false;

    private readonly maxWidthActionsCard = 338;

    constructor(
            private _host: HostListenerService,
            private _el: ElementRef,
    ) {
    }

    @ViewChild('actionsCard') set content(content: MatCard) {
        if (content && this.showActions) {
            this.updateActionsCardWidth();
        }
    }

    get mobileMode$() {
        return this._host.isMobileMode();
    }

    get tabletMode$() {
        return this._host.isTabletMode();
    }

    get desktopMode$() {
        return this._host.isDesktopMode();
    }

    get loading$() {
        return this.loadingSubject$.asObservable();
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('showActions' in changes) {
            this.stackedClass = !this.showActions;
        }
    }

    ngOnInit() {
        this._host.isDesktopMode().pipe(untilDestroyed(this)).subscribe({
            next: (desktopMode) => this.isDesktopMode = desktopMode,
        });

        this._host.isTabletMode().pipe(untilDestroyed(this)).subscribe({
            next: (tabletMode) => this.centeredClass = !this.showActions || tabletMode,
        });

        fromEvent(window, 'resize').pipe(untilDestroyed(this)).subscribe({
            next: () => this.updateActionsCardWidth(),
        });
    }

    doAction(action: string) {
        if (this.loadingSubject$.getValue()) {
            return;
        }

        const cardAction = this.actions.find((a) => a.action === action);
        this.actionClicked.emit(cardAction);
    }

    private updateActionsCardWidth() {
        const containerWidth = this._el.nativeElement.clientWidth;
        if (containerWidth > 0) {
            const actionsInnerCardElement = this._el.nativeElement.querySelector('.actions-card-inner');
            const attachmentsCardElement = this._el.nativeElement.querySelector('.attachments-card .mat-mdc-card');

            const width = (containerWidth * 0.75);
            const innerCardWidth  = (width - 24) < this.maxWidthActionsCard ? (width - 24) : this.maxWidthActionsCard;
            const attachmentsCardWidth = (width - 12) < this.maxWidthActionsCard ? (width - 12) : 'none';

            if (!this.centeredClass && actionsInnerCardElement) {
                actionsInnerCardElement.style['max-width'] = `${innerCardWidth}px`;
                actionsInnerCardElement.style.position = 'fixed';
            } else if (actionsInnerCardElement) {
                actionsInnerCardElement.style['max-width'] = 'none';
            }

            if (attachmentsCardElement) {
                attachmentsCardElement.style['max-width'] = !this.isDesktopMode ? `${attachmentsCardWidth}px` : 'none';
            }
        }
    }
}
